import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtRelative, fmtValue } from "@/lib/format";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

const KINDS: { kind: string; label: string }[] = [
  { kind: "daily", label: "Daily executive" },
  { kind: "weekly", label: "Weekly management" },
  { kind: "monthly", label: "Monthly board" },
];

const DSTATUS: Record<string, string> = {
  delivered: "text-emerald-300",
  skipped: "text-slate-400",
  failed: "text-rose-300",
};

export default function ExecutiveDigestPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");

  const digest = useQuery({ queryKey: ["digest"], queryFn: dashApi.digest, refetchInterval: 60_000 });
  const schedules = useQuery({ queryKey: ["report-schedules"], queryFn: dashApi.reportSchedules });
  const recent = useQuery({ queryKey: ["reports-recent"], queryFn: () => dashApi.reportsRecent() });
  const deliveries = useQuery({ queryKey: ["deliveries"], queryFn: () => dashApi.notificationDeliveries(20) });
  const channels = useQuery({ queryKey: ["notif-channels"], queryFn: dashApi.notificationChannels });
  const targets = useQuery({ queryKey: ["targets-report"], queryFn: dashApi.targetsReport });

  const [preview, setPreview] = useState<{ kind: string; summary: string } | null>(null);
  const previewM = useMutation({
    mutationFn: (kind: string) => dashApi.reportPreview(kind),
    onSuccess: (p) => setPreview({ kind: p.kind, summary: p.summary }),
  });
  const runM = useMutation({
    mutationFn: (kind: string) => dashApi.reportRun(kind),
    onSuccess: () => qc.invalidateQueries(),
  });

  const open = (id: number, fmt: "html" | "csv") => window.open(dashApi.reportExportUrl(id, fmt), "_blank");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Executive Digest"
        description="Scheduled reports, recent reports, delivery status and KPI target achievement."
      />

      {/* Digest summary */}
      {digest.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Latest report" value={<span className="text-sm">{digest.data.latest_report ? digest.data.latest_report.title : "—"}</span>} sub={digest.data.latest_report ? fmtRelative(digest.data.latest_report.generated_at) : undefined} />
          <Stat label="Next scheduled" value={<span className="text-sm">{digest.data.next_report ? digest.data.next_report.kind : "—"}</span>} sub={digest.data.next_report ? fmtDateTime(digest.data.next_report.next_run_at) : undefined} />
          <Stat label="Critical alerts" value={<span className={digest.data.critical_alerts ? "text-rose-300" : ""}>{digest.data.critical_alerts}</span>} sub={`${digest.data.open_alerts} open total`} />
          <Stat label="Last delivery" value={<span className="text-sm">{digest.data.recent_deliveries[0]?.status ?? "—"}</span>} sub={digest.data.recent_deliveries[0] ? fmtRelative(digest.data.recent_deliveries[0].created_at) : undefined} />
        </div>
      )}

      {/* Generate / preview */}
      <Panel title="Generate a report" subtitle="Preview before sending; admins can run + deliver now">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <div key={k.kind} className="flex items-center gap-1.5 rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5">
              <span className="text-xs text-slate-300">{k.label}</span>
              <button type="button" onClick={() => previewM.mutate(k.kind)} disabled={previewM.isPending} className="rounded bg-ink-700 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-ink-600">Preview</button>
              {isAdmin && (
                <button type="button" onClick={() => runM.mutate(k.kind)} disabled={runM.isPending} className="rounded bg-brand-600 px-2 py-0.5 text-[11px] text-white hover:bg-brand-500 disabled:opacity-60">Run + send</button>
              )}
            </div>
          ))}
        </div>
        {previewM.isPending && <LoadingState label="Generating preview…" />}
        {preview && (
          <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-ink-500/50 bg-ink-900/60 p-3 text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap">{preview.summary}</pre>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Scheduled reports">
          {schedules.isLoading ? <LoadingState /> : schedules.isError ? <ErrorState error={schedules.error} /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Report</th>
                  <th className="pb-2 font-medium">Cadence</th>
                  <th className="pb-2 font-medium">Enabled</th>
                  <th className="pb-2 text-right font-medium">Next run</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {schedules.data?.schedules.map((s) => (
                  <tr key={s.code} className="text-slate-300">
                    <td className="py-2">{s.kind}</td>
                    <td className="py-2 text-slate-400">{s.cadence} @ {String(s.hour).padStart(2, "0")}:00</td>
                    <td className="py-2">{s.enabled ? <StatusPill status="ok" label="on" /> : <StatusPill status="not_configured" label="off" />}</td>
                    <td className="py-2 text-right text-xs text-slate-500">{s.next_run_at ? fmtDateTime(s.next_run_at) : "pending first tick"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="KPI target achievement">
          {targets.isLoading ? <LoadingState /> : !targets.data?.targets.length ? (
            <EmptyState compact>No KPI targets configured.</EmptyState>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Target</th>
                  <th className="pb-2 text-right font-medium">Actual</th>
                  <th className="pb-2 text-right font-medium">Target</th>
                  <th className="pb-2 text-right font-medium">%</th>
                  <th className="pb-2 text-right font-medium">History</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {targets.data.targets.map((t) => (
                  <tr key={t.key} className="text-slate-300">
                    <td className="py-2">{t.label}</td>
                    <td className={`py-2 text-right tabular-nums ${t.on_track ? "text-emerald-300" : "text-amber-300"}`}>{fmtValue(t.actual, t.unit === "EUR" ? "EUR" : "count")}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{fmtValue(t.target, t.unit === "EUR" ? "EUR" : "count")}</td>
                    <td className="py-2 text-right tabular-nums">{t.pct_achieved ?? "—"}%</td>
                    <td className="py-2 text-right text-xs">{t.history.length ? `${t.achieved_count}✓ / ${t.missed_count}✗` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <Panel title="Recent reports">
        {recent.isLoading ? <LoadingState /> : !recent.data?.reports.length ? (
          <EmptyState>No reports generated yet. Use “Run + send” or wait for the next scheduled run.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Report</th>
                <th className="pb-2 font-medium">Period</th>
                <th className="pb-2 font-medium">Generated</th>
                <th className="pb-2 font-medium">Trigger</th>
                <th className="pb-2 text-right font-medium">Export</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {recent.data.reports.map((r) => (
                <tr key={r.id} className="text-slate-300">
                  <td className="py-2">{r.title}</td>
                  <td className="py-2 text-slate-400">{r.period_start} → {r.period_end}</td>
                  <td className="py-2 text-xs text-slate-500">{fmtRelative(r.generated_at)}</td>
                  <td className="py-2 text-slate-400">{r.trigger}</td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={() => open(r.id, "html")} className="rounded border border-ink-500/60 bg-ink-700 px-2 py-0.5 text-[11px] hover:bg-ink-600">Print / PDF</button>
                    <button type="button" onClick={() => open(r.id, "csv")} className="ml-1 rounded border border-ink-500/60 bg-ink-700 px-2 py-0.5 text-[11px] hover:bg-ink-600">CSV</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Delivery history">
          {deliveries.isLoading ? <LoadingState /> : !deliveries.data?.deliveries.length ? (
            <EmptyState compact>No deliveries yet.</EmptyState>
          ) : (
            <ul className="divide-y divide-ink-500/40">
              {deliveries.data.deliveries.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-slate-300">{d.subject}</span>
                  <span className="text-[11px] text-slate-500">{d.channel_type}/{d.target}</span>
                  <span className={`text-[11px] ${DSTATUS[d.status] ?? "text-slate-400"}`}>{d.status}</span>
                  <span className="text-[11px] text-slate-600">{fmtRelative(d.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Notification channels">
          {channels.isLoading ? <LoadingState /> : (
            <ul className="divide-y divide-ink-500/40">
              {channels.data?.channels.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-slate-200">{c.name}</span>
                  <span className="text-xs text-slate-400">{c.type}</span>
                  {c.enabled ? <StatusPill status="ok" label="enabled" /> : <StatusPill status="not_configured" label="off" />}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[11px] text-slate-600">Email/webhook deliver only when configured; the activity-log channel always records. No external provider required.</p>
        </Panel>
      </div>
    </div>
  );
}
